/**
 * User Data Inspector - 유저 관련 데이터 조회 및 삭제 도구
 * 
 * User ID를 입력하면 해당 유저와 관련된 모든 데이터를 컬렉션별로 집계하여 표시.
 * 삭제 전 영향 범위를 파악하거나, Console 삭제 후 고아 데이터를 확인할 때 사용.
 */

import { Trans } from '@lingui/macro';
import {
    Search, User, MessageSquare, Bell, DoorOpen, Bot,
    Link2, Users, Building2, Heart, Shield, Smartphone,
    Apple, Loader2, AlertTriangle, Trash2, Copy, Check, RotateCcw
} from 'lucide-react';
import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AdminFire } from '../../services/SuperAdminFirebase';
import {
    collection, collectionGroup, getDocs, query, where,
    doc, getDoc, deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// ── Types ────────────────────────────────────────────────────

interface DataCategory {
    id: string;
    label: string;
    icon: React.ElementType;
    color: string;
    count: number;
    details?: string;
}

interface UserInfo {
    id: string;
    name: string;
    email: string;
    photoUrl?: string | null;
    lang?: string;
    created?: any;
    lastLoginAt?: any;
}

interface WorkspaceDetail {
    id: string;
    name: string;
    role: string;
}

interface RoomDetail {
    id: string;
    name: string;
    type: '혼자' | '1:1' | '그룹';
    isCreator: boolean;
    memberCount: number;
    workspaceId?: string;
}

interface SoribotDetail {
    id: string;
    name: string;
    role: '소유자' | '참여자';
    userCount: number;
    workspaceId?: string;
}

interface ScanResult {
    user: UserInfo | null;
    authExists: boolean;
    firestoreExists: boolean;
    categories: DataCategory[];
    totalCount: number;
    scanTime: number;
    workspaces: WorkspaceDetail[];
    rooms: RoomDetail[];
    soribots: SoribotDetail[];
}

// ── Component ────────────────────────────────────────────────

const DevAdminUsers: React.FC = () => {
    const [userId, setUserId] = useState('');
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState<ScanResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [cleaningUp, setCleaningUp] = useState(false);
    const [cleanupResult, setCleanupResult] = useState<any>(null);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

    const store = AdminFire.store;

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // ── Scan Logic ────────────────────────────────────────────

    const scanUser = useCallback(async () => {
        const trimmedId = userId.trim();
        if (!trimmedId) return;

        setScanning(true);
        setError(null);
        setResult(null);

        const startTime = Date.now();
        const categories: DataCategory[] = [];

        try {
            // 1. Firestore 유저 문서 확인
            const userDocRef = doc(store, 'users', trimmedId);
            const userDoc = await getDoc(userDocRef);
            const firestoreExists = userDoc.exists();
            let userInfo: UserInfo | null = null;

            if (firestoreExists) {
                const data = userDoc.data();
                userInfo = {
                    id: trimmedId,
                    name: data.name || '(이름 없음)',
                    email: data.email || '(이메일 없음)',
                    photoUrl: data.photoUrl,
                    lang: data.lang,
                    created: data.created,
                    lastLoginAt: data.lastLoginAt,
                };
            }

            // 2. 각 컬렉션별 데이터 수 집계 (병렬 실행)
            const [
                rooms, messages, notifications,
                memberSnap, soribotOwner, soribotShared,
                invites,
                fcmTokens, favorites, userblocks
            ] = await Promise.all([
                // Rooms: userIds에 포함
                getDocs(query(collection(store, 'rooms'), where('userIds', 'array-contains', trimmedId))),
                // Messages: user.id == userId
                getDocs(query(collection(store, 'messages'), where('user.id', '==', trimmedId))),
                // Notifications: toUserId == userId
                getDocs(query(collection(store, 'notifications'), where('toUserId', '==', trimmedId))),
                // Workspace Members: collectionGroup
                getDocs(query(collectionGroup(store, 'members'), where('id', '==', trimmedId))),
                // Soribots owned
                getDocs(query(collection(store, 'soribots'), where('owner.id', '==', trimmedId))),
                // Soribots shared
                getDocs(query(collection(store, 'soribots'), where('userIds', 'array-contains', trimmedId))),
                // Room Invites
                getDocs(query(collection(store, 'roominvites'), where('creator.id', '==', trimmedId))),
                // Sub-collections
                getDocs(collection(store, 'users', trimmedId, 'userfmctokens')),
                getDocs(collection(store, 'users', trimmedId, 'favorites')),
                getDocs(collection(store, 'users', trimmedId, 'userblocks')),
            ]);

            // 개인방(Room ID == User ID) 직접 조회 (userIds가 비어있으면 array-contains에 안 잡힘)
            const personalRoomDoc = await getDoc(doc(store, 'rooms', trimmedId));
            const allRoomDocs = [...rooms.docs];
            if (personalRoomDoc.exists() && !allRoomDocs.some(d => d.id === trimmedId)) {
                allRoomDocs.push(personalRoomDoc as any);
            }

            // Room 상세 분류
            let aloneRooms = 0;
            let groupRooms = 0;
            let oneToOneRooms = 0;
            const roomDetails: RoomDetail[] = [];
            allRoomDocs.forEach(d => {
                const room = d.data();
                const otherCount = (room.userIds || []).filter((id: string) => id !== trimmedId).length;
                let roomType: RoomDetail['type'] = '그룹';
                if (otherCount === 0) { aloneRooms++; roomType = '혼자'; }
                else if (room.isOneToOne) { oneToOneRooms++; roomType = '1:1'; }
                else { groupRooms++; }
                roomDetails.push({
                    id: d.id,
                    name: room.name || (room.isOneToOne ? '1:1 채팅' : '(이름 없음)'),
                    type: roomType,
                    isCreator: room.creatorId === trimmedId,
                    memberCount: (room.userIds || []).length,
                    workspaceId: room.workspaceId,
                });
            });

            // Workspace 상세
            const workspaceDetails: WorkspaceDetail[] = [];
            for (const d of memberSnap.docs) {
                const wsId = d.ref.parent.parent?.id;
                if (!wsId) continue;
                const memberData = d.data();
                let wsName = wsId;
                try {
                    const wsDoc = await getDoc(doc(store, 'workspaces', wsId));
                    if (wsDoc.exists()) wsName = wsDoc.data().name || wsId;
                } catch { /* ignore */ }
                workspaceDetails.push({
                    id: wsId,
                    name: wsName,
                    role: memberData.role || 'member',
                });
            }

            // Soribot 상세 (owner + shared 통합, 중복 제거)
            const soribotMap = new Map<string, SoribotDetail>();
            soribotOwner.docs.forEach(d => {
                const bot = d.data();
                soribotMap.set(d.id, {
                    id: d.id,
                    name: bot.name || '(이름 없음)',
                    role: '소유자',
                    userCount: (bot.userIds || []).length,
                    workspaceId: bot.workspaceId,
                });
            });
            soribotShared.docs.forEach(d => {
                if (!soribotMap.has(d.id)) {
                    const bot = d.data();
                    soribotMap.set(d.id, {
                        id: d.id,
                        name: bot.name || '(이름 없음)',
                        role: '참여자',
                        userCount: (bot.userIds || []).length,
                        workspaceId: bot.workspaceId,
                    });
                }
            });
            const soribotDetails = Array.from(soribotMap.values());

            categories.push(
                {
                    id: 'rooms',
                    label: 'Rooms (참여 중)',
                    icon: DoorOpen,
                    color: 'bg-blue-500',
                    count: allRoomDocs.length,
                    details: `혼자 ${aloneRooms} / 1:1 ${oneToOneRooms} / 그룹 ${groupRooms}`,
                },
                {
                    id: 'messages',
                    label: 'Messages (작성)',
                    icon: MessageSquare,
                    color: 'bg-emerald-500',
                    count: messages.size,
                    details: (() => {
                        const oneYearAgo = new Date();
                        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                        let willDelete = 0, willAnonymize = 0, alreadyProcessed = 0;
                        messages.docs.forEach(d => {
                            const data = d.data();
                            const email = data.user?.email;
                            const isAnonymized = (email == null || email === 'deleted@email.com');
                            const raw = data.created;
                            const created = raw?.toDate?.() ?? (typeof raw === 'string' ? new Date(raw) : raw);
                            if (created && created < oneYearAgo) {
                                willDelete++;
                            } else if (isAnonymized) {
                                alreadyProcessed++;
                            } else {
                                willAnonymize++;
                            }
                        });
                        const parts: string[] = [];
                        if (willDelete > 0) parts.push(`삭제예정(1년↑) ${willDelete}`);
                        if (willAnonymize > 0) parts.push(`익명화예정 ${willAnonymize}`);
                        if (alreadyProcessed > 0) parts.push(`처리완료 ${alreadyProcessed}`);
                        return parts.join(' / ') || undefined;
                    })(),
                },
                {
                    id: 'notifications',
                    label: 'Notifications',
                    icon: Bell,
                    color: 'bg-amber-500',
                    count: notifications.size,
                },
                {
                    id: 'workspaces',
                    label: 'Workspace Memberships',
                    icon: Building2,
                    color: 'bg-purple-500',
                    count: memberSnap.size,
                    details: memberSnap.docs.map(d => {
                        const wsId = d.ref.parent.parent?.id;
                        const role = d.data().role || 'member';
                        return `${wsId?.substring(0, 8)}.. (${role})`;
                    }).join(', '),
                },
                {
                    id: 'soribots-owned',
                    label: 'Soribots (소유)',
                    icon: Bot,
                    color: 'bg-pink-500',
                    count: soribotOwner.size,
                },
                {
                    id: 'soribots-shared',
                    label: 'Soribots (공유 참여)',
                    icon: Bot,
                    color: 'bg-pink-300',
                    count: soribotShared.size,
                },
                {
                    id: 'invites',
                    label: 'Room Invites (생성)',
                    icon: Link2,
                    color: 'bg-teal-500',
                    count: invites.size,
                },
                {
                    id: 'fcm-tokens',
                    label: 'FCM Tokens',
                    icon: Smartphone,
                    color: 'bg-slate-500',
                    count: fcmTokens.size,
                },
                {
                    id: 'favorites',
                    label: 'Favorites (즐겨찾기)',
                    icon: Heart,
                    color: 'bg-red-400',
                    count: favorites.size,
                },
                {
                    id: 'userblocks',
                    label: 'User Blocks (차단)',
                    icon: Shield,
                    color: 'bg-slate-400',
                    count: userblocks.size,
                },
            );

            // Apple Token 확인
            try {
                const appleTokenDoc = await getDoc(doc(store, 'users', trimmedId, 'appletokens', trimmedId));
                categories.push({
                    id: 'apple-token',
                    label: 'Apple Token',
                    icon: Apple,
                    color: 'bg-gray-800',
                    count: appleTokenDoc.exists() ? 1 : 0,
                });
            } catch {
                // appletokens 컬렉션이 없을 수 있음
            }

            const totalCount = categories.reduce((sum, c) => sum + c.count, 0);

            setResult({
                user: userInfo,
                authExists: false, // Client SDK로는 Auth 확인 불가, 서버 함수 필요
                firestoreExists,
                categories,
                totalCount,
                scanTime: Date.now() - startTime,
                workspaces: workspaceDetails,
                rooms: roomDetails,
                soribots: soribotDetails,
            });
        } catch (err: any) {
            setError(err.message || '스캔 중 오류가 발생했습니다.');
        } finally {
            setScanning(false);
        }
    }, [userId, store]);

    // ── Delete Firestore User Doc ────────────────────────────

    const deleteUserDoc = useCallback(async () => {
        if (!userId.trim() || !result?.firestoreExists) return;
        if (!confirm(`정말 users/${userId.trim()} 문서를 삭제하시겠습니까?\n\nonDeleteUserTrigger가 자동으로 관련 데이터를 정리합니다.`)) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(store, 'users', userId.trim()));
            setResult(prev => prev ? { ...prev, firestoreExists: false } : null);
            alert('유저 문서가 삭제되었습니다.\nonDeleteUserTrigger가 백그라운드에서 관련 데이터를 정리합니다.');
        } catch (err: any) {
            alert(`삭제 실패: ${err.message}`);
        } finally {
            setDeleting(false);
        }
    }, [userId, result, store]);

    // ── Orphan Cleanup ────────────────────────────────────────

    const cleanupOrphan = useCallback(async () => {
        const trimmedId = userId.trim();
        if (!trimmedId) return;
        if (!confirm(`고아 데이터를 정리하시겠습니까?\n\nUser ID: ${trimmedId}\n\nRoom 제거, 메시지 익명화/삭제, 알림·멤버십·봇 정리가 진행됩니다.`)) return;

        setCleaningUp(true);
        setCleanupResult(null);
        setResult(null);
        try {
            const fn = httpsCallable(AdminFire.functions, 'cleanupOrphanUser');
            const res = await fn({ userId: trimmedId });
            setCleanupResult(res.data);
            // 정리 후 다시 스캔
            scanUser();
        } catch (err: any) {
            alert(`정리 실패: ${err.message}`);
        } finally {
            setCleaningUp(false);
        }
    }, [userId, scanUser]);

    // ── Copy to clipboard ────────────────────────────────────

    const copyReport = useCallback(() => {
        if (!result) return;
        const lines = [
            `== User Data Report ==`,
            `User ID: ${userId.trim()}`,
            `Firestore: ${result.firestoreExists ? '존재' : '없음'}`,
            result.user ? `Name: ${result.user.name}` : '',
            result.user ? `Email: ${result.user.email}` : '',
            '',
            ...result.categories
                .filter(c => c.count > 0)
                .map(c => `${c.label}: ${c.count}${c.details ? ` (${c.details})` : ''}`),
            '',
            `Total: ${result.totalCount} items`,
            `Scan time: ${result.scanTime}ms`,
        ].filter(Boolean).join('\n');

        navigator.clipboard.writeText(lines);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [result, userId]);

    // ── Render ────────────────────────────────────────────────

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <h1 className="text-xl font-bold text-slate-900">
                    User Data Inspector
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                    유저 ID로 관련 데이터를 조회하고 삭제 영향 범위를 확인합니다
                </p>
            </div>

            {/* Search Bar */}
            <div className="px-6 py-4 bg-white border-b border-slate-100">
                <div className="flex gap-3 max-w-2xl">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && scanUser()}
                            placeholder="User ID를 입력하세요 (예: abc123def456)"
                            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm 
                                     focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500
                                     font-mono placeholder:font-sans"
                        />
                    </div>
                    <Button
                        onClick={scanUser}
                        disabled={scanning || !userId.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                    >
                        {scanning ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <Search className="w-4 h-4 mr-2" />
                        )}
                        스캔
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {/* Error */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 max-w-4xl">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        <div>
                            <h4 className="font-medium text-red-800">오류 발생</h4>
                            <p className="text-sm text-red-600 mt-1">{error}</p>
                        </div>
                    </div>
                )}

                {/* Results */}
                {result && (
                    <div className="max-w-4xl space-y-6">
                        {/* User Info Card */}
                        <Card className="overflow-hidden">
                            <CardContent className="p-0">
                                <div className="flex items-stretch">
                                    <div className={`w-20 ${result.firestoreExists ? 'bg-emerald-500' : 'bg-red-400'} flex items-center justify-center`}>
                                        <User className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="flex-1 p-5">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-lg font-bold text-slate-900">
                                                        {result.user?.name || '(문서 없음)'}
                                                    </h3>
                                                    <Badge variant={result.firestoreExists ? 'default' : 'destructive'}>
                                                        {result.firestoreExists ? 'Firestore 존재' : 'Firestore 없음'}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-slate-500 font-mono">{userId.trim()}</p>
                                                {result.user && (
                                                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                                        <span>{result.user.email}</span>
                                                        {result.user.lang && <span>🌐 {result.user.lang}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={copyReport}
                                                >
                                                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                                    <span className="ml-1.5">{copied ? '복사됨' : '리포트 복사'}</span>
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={scanUser}
                                                >
                                                    <RotateCcw className="w-4 h-4" />
                                                    <span className="ml-1.5">새로고침</span>
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Summary */}
                                        <div className="mt-4 flex items-center gap-6 text-sm">
                                            <div className="px-3 py-1.5 bg-slate-100 rounded-md">
                                                전체 관련 데이터: <strong className="text-emerald-700">{result.totalCount}건</strong>
                                            </div>
                                            <div className="text-slate-400">
                                                스캔 시간: {result.scanTime}ms
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Data Categories Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                            {result.categories.map((cat) => {
                                const Icon = cat.icon;
                                const hasData = cat.count > 0;
                                return (
                                    <Card
                                        key={cat.id}
                                        className={`overflow-hidden transition-all ${hasData ? 'ring-1 ring-slate-200' : 'opacity-50'}`}
                                    >
                                        <CardContent className="p-0">
                                            <div className="flex items-center">
                                                <div className={`w-12 ${cat.color} min-h-16 flex items-center justify-center flex-shrink-0`}>
                                                    <Icon className="w-5 h-5 text-white" />
                                                </div>
                                                <div className="flex-1 p-3 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-medium text-slate-500 truncate">
                                                            {cat.label}
                                                        </span>
                                                        <span className={`text-lg font-bold ${hasData ? 'text-slate-900' : 'text-slate-300'}`}>
                                                            {cat.count}
                                                        </span>
                                                    </div>
                                                    {cat.details && hasData && (
                                                        <p className="text-meta-sm text-slate-400 mt-0.5 truncate">
                                                            {cat.details}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Detail Lists */}
                        {(result.workspaces.length > 0 || result.rooms.length > 0 || result.soribots.length > 0) && (
                            <div className="space-y-3">
                                {/* Workspaces */}
                                {result.workspaces.length > 0 && (
                                    <Card className="overflow-hidden">
                                        <button
                                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                            onClick={() => toggleSection('workspaces')}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                                                    <Building2 className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="font-medium text-slate-900">워크스페이스</span>
                                                <Badge variant="secondary">{result.workspaces.length}</Badge>
                                            </div>
                                            <span className={`text-slate-400 transition-transform ${expandedSections['workspaces'] ? 'rotate-180' : ''}`}>▼</span>
                                        </button>
                                        {expandedSections['workspaces'] && (
                                            <div className="border-t border-slate-100">
                                                {result.workspaces.map(ws => (
                                                    <div key={ws.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium text-slate-800 truncate">{ws.name}</div>
                                                                <div className="text-xs text-slate-400 font-mono truncate">{ws.id}</div>
                                                            </div>
                                                        </div>
                                                        <Badge
                                                            variant={ws.role === 'owner' ? 'default' : 'outline'}
                                                            className={ws.role === 'owner' ? 'bg-purple-600' : ws.role === 'admin' ? 'border-purple-300 text-purple-600' : ''}
                                                        >
                                                            {ws.role}
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                )}

                                {/* Rooms */}
                                {result.rooms.length > 0 && (
                                    <Card className="overflow-hidden">
                                        <button
                                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                            onClick={() => toggleSection('rooms')}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                                    <DoorOpen className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="font-medium text-slate-900">참여 중인 방</span>
                                                <Badge variant="secondary">{result.rooms.length}</Badge>
                                            </div>
                                            <span className={`text-slate-400 transition-transform ${expandedSections['rooms'] ? 'rotate-180' : ''}`}>▼</span>
                                        </button>
                                        {expandedSections['rooms'] && (
                                            <div className="border-t border-slate-100">
                                                {result.rooms.map(room => (
                                                    <div key={room.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${room.type === '혼자' ? 'bg-slate-300' :
                                                                room.type === '1:1' ? 'bg-sky-400' : 'bg-blue-500'
                                                                }`} />
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium text-slate-800 truncate">
                                                                    {room.name}
                                                                </div>
                                                                <div className="text-xs text-slate-400 font-mono truncate">
                                                                    {room.id}
                                                                    {room.workspaceId && <span className="ml-2 text-slate-300">· {room.workspaceId}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-slate-400">
                                                                <Users className="w-3 h-3 inline mr-0.5" />{room.memberCount}
                                                            </span>
                                                            <Badge variant="outline" className={`text-meta-sm px-1.5 ${room.type === '혼자' ? 'border-slate-200 text-slate-400' :
                                                                room.type === '1:1' ? 'border-sky-200 text-sky-600' : 'border-blue-200 text-blue-600'
                                                                }`}>
                                                                {room.type}
                                                            </Badge>
                                                            {room.isCreator && (
                                                                <Badge className="bg-amber-500 text-meta-sm px-1.5">생성자</Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                )}

                                {/* Soribots */}
                                {result.soribots.length > 0 && (
                                    <Card className="overflow-hidden">
                                        <button
                                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                            onClick={() => toggleSection('soribots')}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center">
                                                    <Bot className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="font-medium text-slate-900">Soribots</span>
                                                <Badge variant="secondary">{result.soribots.length}</Badge>
                                            </div>
                                            <span className={`text-slate-400 transition-transform ${expandedSections['soribots'] ? 'rotate-180' : ''}`}>▼</span>
                                        </button>
                                        {expandedSections['soribots'] && (
                                            <div className="border-t border-slate-100">
                                                {result.soribots.map(bot => (
                                                    <div key={bot.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bot.role === '소유자' ? 'bg-pink-500' : 'bg-pink-300'
                                                                }`} />
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium text-slate-800 truncate">{bot.name}</div>
                                                                <div className="text-xs text-slate-400 font-mono truncate">
                                                                    {bot.id}
                                                                    {bot.workspaceId && <span className="ml-2 text-slate-300">· {bot.workspaceId}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-slate-400">
                                                                <Users className="w-3 h-3 inline mr-0.5" />{bot.userCount}
                                                            </span>
                                                            <Badge
                                                                variant={bot.role === '소유자' ? 'default' : 'outline'}
                                                                className={bot.role === '소유자' ? 'bg-pink-600 text-meta-sm px-1.5' : 'border-pink-200 text-pink-500 text-meta-sm px-1.5'}
                                                            >
                                                                {bot.role}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        {result.firestoreExists && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                                    <div className="flex-1">
                                        <h4 className="font-medium text-red-800">유저 문서 삭제</h4>
                                        <p className="text-sm text-red-600 mt-1">
                                            Firestore 유저 문서를 삭제하면 <code className="bg-red-100 px-1 rounded">onDeleteUserTrigger</code>가
                                            자동으로 관련 데이터를 정리합니다 (Room 정리, 메시지 익명화, 알림 삭제 등).
                                        </p>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            className="mt-3"
                                            onClick={deleteUserDoc}
                                            disabled={deleting}
                                        >
                                            {deleting ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <Trash2 className="w-4 h-4 mr-2" />
                                            )}
                                            유저 문서 삭제 (Cascade 정리 발동)
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Orphan Detection + Cleanup */}
                        {!result.firestoreExists && result.totalCount > 0 && (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <div className="flex-1">
                                        <h4 className="font-medium text-amber-800">⚠️ 고아 데이터 감지</h4>
                                        <p className="text-sm text-amber-700 mt-1">
                                            Firestore 유저 문서가 없지만 관련 데이터가 {result.totalCount}건 남아있습니다.
                                            Console에서 Auth만 삭제했거나, 이전 삭제가 불완전했을 수 있습니다.
                                        </p>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            className="mt-3"
                                            onClick={cleanupOrphan}
                                            disabled={cleaningUp}
                                        >
                                            {cleaningUp ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <Trash2 className="w-4 h-4 mr-2" />
                                            )}
                                            고아 데이터 정리 (Cloud Function)
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Cleanup Result (독립 표시) */}
                        {cleanupResult && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="p-3 bg-white border border-green-200 rounded text-xs font-mono space-y-1">
                                    <p className="font-semibold text-green-800">✅ 정리 완료</p>
                                    {cleanupResult.roomsDeleted > 0 && <p>Room 삭제: {cleanupResult.roomsDeleted}</p>}
                                    {cleanupResult.roomsUpdated > 0 && <p>Room 업데이트: {cleanupResult.roomsUpdated}</p>}
                                    {cleanupResult.messagesAnonymized > 0 && <p>메시지 익명화: {cleanupResult.messagesAnonymized}</p>}
                                    {cleanupResult.messagesDeleted > 0 && <p>메시지 삭제 (1년↑): {cleanupResult.messagesDeleted}</p>}
                                    {cleanupResult.workspaceMemberships > 0 && <p>워크스페이스 멤버십: {cleanupResult.workspaceMemberships}</p>}
                                    {cleanupResult.notifications > 0 && <p>알림: {cleanupResult.notifications}</p>}
                                    {cleanupResult.soribots > 0 && <p>소리봇: {cleanupResult.soribots}</p>}
                                    {cleanupResult.invites > 0 && <p>초대 링크: {cleanupResult.invites}</p>}
                                    {cleanupResult.subCollections > 0 && <p>서브컬렉션: {cleanupResult.subCollections}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty State */}
                {!result && !error && !scanning && (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                        <Search className="w-12 h-12 mb-3 opacity-30" />
                        <p className="text-sm">User ID를 입력하고 스캔을 시작하세요</p>
                        <p className="text-xs mt-1 opacity-60">
                            삭제 전 영향 범위 확인 · Console 삭제 후 고아 데이터 점검
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminUsers;
